import { useEffect, useRef } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useMediaSettingsStore, useRoomActionsStore, useRoomStore, useVideoStreamStore } from '@src/stores';
import {
  getUserMediaWithFallback,
  getResolutionPreset,
  buildVideoConstraints,
  DEFAULT_VIDEO_RESOLUTION,
  VIDEO_RESOLUTION_PRESETS,
} from '@src/utils/videoConstraints.utils';
import { handleCameraError } from '@src/helpers';

import { EMediaType } from '@src/types';

/**
 * Hook to handle global media side effects, such as updating the preview stream
 * when video resolution changes.
 *
 * This should be mounted ONE TIME at the root application or layout level.
 */
export const useMediaEffects = () => {
  // Ref to skip effects on initial mount
  const isInitialResolutionMountRef = useRef(true);

  // We only need to subscribe to the trigger: selectedVideoResolution
  const selectedVideoResolution = useMediaSettingsStore(
    useShallow((state) => state.mediaSettings.selectedVideoResolution)
  );

  // Handle video resolution changes for preview stream (lobby/settings)
  useEffect(() => {
    // Skip on initial mount
    if (isInitialResolutionMountRef.current) {
      isInitialResolutionMountRef.current = false;
      return;
    }

    const updatePreviewStream = async () => {
      // Read latest state inside the effect to avoid dependency warnings
      const { isJoinedRoom } = useRoomStore.getState();
      const { isCamClosed } = useRoomActionsStore.getState().camSettings;
      const { selectedVideoInputDevice, isVideoAllowed } = useMediaSettingsStore.getState().mediaSettings;
      const { originalStream, setOriginalStream } = useVideoStreamStore.getState();

      // Only update preview if: not in meeting, has stream, camera open, video allowed
      if (!isJoinedRoom && originalStream && selectedVideoInputDevice?.deviceId && isVideoAllowed && !isCamClosed) {
        console.log('[useMediaEffects] Resolution changed to:', selectedVideoResolution);

        const videoTrack = originalStream.getVideoTracks()[0];

        // Strategy 1: Try to apply constraints to existing track (Smooth, no flicker)
        if (videoTrack && videoTrack.readyState === 'live') {
          try {
            const preset =
              getResolutionPreset(selectedVideoResolution) ?? getResolutionPreset(DEFAULT_VIDEO_RESOLUTION)!;

            // Build new constraints - maintain current device
            const constraints = buildVideoConstraints(preset, selectedVideoInputDevice.deviceId, {
              useExactDeviceId: true,
            });

            const videoConstraints = constraints.video as MediaTrackConstraints;

            console.log('[useMediaEffects] Applying constraints to preview:', {
              width: videoConstraints.width,
              height: videoConstraints.height,
            });

            await videoTrack.applyConstraints(videoConstraints);

            // VERIFICATION LOG
            const activeSettings = videoTrack.getSettings();

            // UX: If hardware couldn't meet the target resolution, revert the selection to the actual resolution
            if (activeSettings.width && activeSettings.height) {
              const targetPreset = getResolutionPreset(selectedVideoResolution);
              // Check if we got significantly less than requested (tolerance of 100px)
              if (targetPreset && activeSettings.width < targetPreset.width - 100) {
                const actualPreset = VIDEO_RESOLUTION_PRESETS.find(
                  (p) => Math.abs(p.width - activeSettings.width!) < 50
                );

                if (actualPreset && actualPreset.value !== selectedVideoResolution) {
                  console.warn(
                    `[useMediaEffects] Target ${selectedVideoResolution} (${targetPreset.width}px) failed. Got ${activeSettings.width}px. Reverting to ${actualPreset.value}.`
                  );
                  useMediaSettingsStore.getState().setMediaSettings({ selectedVideoResolution: actualPreset.value });
                }
              }
            }

            return; // Success! No need to replace stream
          } catch (error) {
            console.warn(
              '[useMediaEffects] applyConstraints failed for preview, falling back to replace stream:',
              error
            );
          }
        }

        // Strategy 2: Fallback to getting a fresh stream (Stop -> GetNew)
        try {
          // Stop existing stream tracks
          originalStream.getTracks().forEach((track) => track.stop());

          // Get new stream with new resolution
          const newStream = await getUserMediaWithFallback(selectedVideoInputDevice.deviceId, selectedVideoResolution);
          setOriginalStream(newStream);
        } catch (error) {
          console.error('[useMediaEffects] Failed to update preview stream:', error);
          handleCameraError(error as Error, EMediaType.camera);
        }
      }
    };

    updatePreviewStream();
  }, [selectedVideoResolution]);
};
